/**
 * Automatic colour correction of the photos for print, port of the photo-auto.js study.
 * Two layers :
 *  1. a correction of each photo (dark, dull), decided on a 64 px thumbnail ;
 *  2. a paper compensation applied to every photo, anticipating what printing loses (mid tones
 *     getting darker, colours fading) so that the print looks like what the customer saw on screen.
 *
 * Used by the screenshot app only (the print files), on creations with to_enhance = 1 : the
 * stage Photo component shows the corrected picture instead of the original one, its effect,
 * brightness / saturation / contrast filters and its mask still applying on top.
 *
 * Every parameter comes from models.Settings (enhance*). Photos are corrected one at a time, the
 * result (a blob url) is cached by source url. Any failure (no CORS, canvas too big...) answers the
 * original url : a screenshot is never blocked by the correction.
 *
 * Transparency (PNG) is kept : the transparent pixels are left out of the analysis and of the
 * sharpening, and such a photo comes out as a PNG rather than a JPEG, which has no alpha channel.
 */
fx.ns("enhance", class PhotoEnhancer {

    constructor(options)
    {
        this.options = Object.assign({}, enhance.PhotoEnhancer.defaults, options || {});

        this._cache = {};
        this._queue = Promise.resolve();
    }

    static get defaults()
    {
        return {
            paperBlack : 0.05,
            paperWhite : 0.97,
            dotGain : 1.05,
            paperSaturation : 0.93,
            paperBlur : 0.25,
            compensation : 1,
            punch : 0.08,
            sharpen : 0.4,
            sharpenRadius : 0.7,
            sharpenThreshold : 3,
            darkThreshold : 0.36,
            targetMedian : 0.43,
            gammaMin : 0.78,
            dullThreshold : 0.13,
            grayThreshold : 0.035,
            vibranceMax : 0.25,
            vibranceCap : 0.6,
            analysisSize : 64,
            maxSize : 6000,
            jpegQuality : 0.95,
            loadTimeout : 30000
        };
    }

    // one instance for the app, set up from models.Settings (enhanceXxx -> xxx)
    static get shared()
    {
        if (!enhance.PhotoEnhancer._shared)
        {
            let settings = fx.get("settings") || {};
            let options = {};

            for (let key in enhance.PhotoEnhancer.defaults)
            {
                let name = "enhance" + key.charAt(0).toUpperCase() + key.slice(1);

                if (settings[name] !== undefined)
                    options[key] = settings[name];
            }

            enhance.PhotoEnhancer._shared = new enhance.PhotoEnhancer(options);
        }

        return enhance.PhotoEnhancer._shared;
    }

    // ?debug-enhance=1 : every photo, and whether the correction is on, logged in the console
    static get debugging()
    {
        return !!(fx.get("debugging") || Utils.getURLParam("debug-enhance"));
    }

    // one line per message, the details as JSON : readable as is in a headless browser's output too
    static log(message, details)
    {
        console.log("[PhotoEnhancer] " + message + (details ? " " + JSON.stringify(details) : ""));
    }

    static luminance(r, g, b)  // 0 - 255
    {
        return 0.2126 * r + 0.7152 * g + 0.0722 * b;
    }

    /**
     * Resolves with the url of the corrected picture (the original url when nothing had to change or
     * when the correction failed).
     *  options.photoCorrection : false leaves out the correction of the photo itself (paper compensation only)
     *  options.simulatePaper : also applies the paper model, to preview on screen what the print will look like
     */
    enhance(url, options)
    {
        options = options || {};

        if (!url)
            return Promise.resolve(url);

        let key = url + "|" + (options.photoCorrection === false ? 0 : 1) + (options.simulatePaper ? "|paper" : "");

        if (!this._cache[key])
        {
            // one photo at a time : a full size photo weighs hundreds of MB while it is being corrected
            let start = Date.now();

            this._cache[key] = this._queue = this._queue
                .then(() => this._process(url, options))
                .catch((error) => {
                    console.warn("PhotoEnhancer : " + url + " left as is (" + (error && error.message) + ")");
                    return { url : url, modified : false, error : error && error.message };
                })
                .then((result) => {
                    if (enhance.PhotoEnhancer.debugging)
                        enhance.PhotoEnhancer.log((result.error ? "failed " : result.modified ? "corrected " : "unchanged ") + url, {
                            photoCorrection : options.photoCorrection !== false,
                            simulatePaper : !!options.simulatePaper,
                            ms : Date.now() - start,
                            stats : result.stats,
                            settings : result.settings
                        });

                    return result;
                });
        }

        return this._cache[key].then((result) => result.url);
    }

    _process(url, options)
    {
        return this._loadImage(url).then((img) => {
            let stats = this.analyse(img);
            let settings = this.diagnose(stats, options);

            if (!options.simulatePaper && settings.gamma === 1 && settings.vibrance === 0 && settings.contrast === 0 && settings.sharpen === 0)
                return { url : url, stats : stats, settings : settings, modified : false };

            let canvas = this._toCanvas(img, this.options.maxSize);
            let ctx = canvas.getContext("2d", { willReadFrequently : true });
            let imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            let transparent = this.hasAlpha(imageData);

            settings.transparent = transparent;

            this.correct(imageData, settings);

            if (options.simulatePaper)
                this.simulatePaper(imageData, transparent);

            ctx.putImageData(imageData, 0, 0);

            return this._toBlobUrl(canvas, transparent).then((blobUrl) => {
                canvas.width = canvas.height = 0; // frees the bitmap now rather than at the next GC

                return { url : blobUrl, stats : stats, settings : settings, modified : true };
            });
        });
    }

    // ---------- 1. Analysis on a thumbnail ----------

    analyse(img)
    {
        let size = this.options.analysisSize;
        let canvas = document.createElement("canvas");
        canvas.width = canvas.height = size;

        let ctx = canvas.getContext("2d", { willReadFrequently : true });
        ctx.imageSmoothingQuality = "medium";
        ctx.drawImage(img, 0, 0, size, size);

        return this.analysePixels(ctx.getImageData(0, 0, size, size).data);
    }

    analysePixels(data)
    {
        let LUM = enhance.PhotoEnhancer.luminance;
        let hist = new Uint32Array(256);
        let n = 0;
        let chromaSum = 0, chromaCount = 0;

        for (let i = 0; i < data.length; i += 4)
        {
            // mostly transparent pixels are not part of the picture (their colour, often black, means nothing)
            if (data[i + 3] < 128)
                continue;

            n++;

            let r = data[i], g = data[i + 1], b = data[i + 2];
            let l = LUM(r, g, b);
            hist[l | 0]++;

            if (l > 25 && l < 230)
            {
                chromaSum += (Math.max(r, g, b) - Math.min(r, g, b)) / 255;
                chromaCount++;
            }
        }

        // nothing opaque enough to judge : neutral figures, no correction of the photo itself
        if (!n)
            return { median : 0.5, p05 : 0, p95 : 1, chroma : 0, empty : true };

        let percentile = (p) => {
            let target = p * n, sum = 0;

            for (let v = 0; v < 256; v++)
            {
                sum += hist[v];

                if (sum >= target)
                    return v / 255;
            }

            return 1;
        };

        return { median : percentile(0.5), p05 : percentile(0.05), p95 : percentile(0.95), chroma : chromaCount ? chromaSum / chromaCount : 0 };
    }

    // ---------- 2. Decision ----------

    diagnose(stats, options)
    {
        let o = this.options;
        let gray = stats.chroma <= o.grayThreshold;

        // correction of the photo itself
        let photoGamma = 1, photoVibrance = 0;

        if (!options || options.photoCorrection !== false)
        {
            if (stats.median < o.darkThreshold && stats.median > 0.02)
            {
                photoGamma = Math.log(o.targetMedian) / Math.log(stats.median);

                if (stats.p95 > 0.85)
                    photoGamma = 1 - (1 - photoGamma) * 0.5; // dark mood probably wanted

                photoGamma = Math.min(1, Math.max(o.gammaMin, photoGamma));
            }

            if (!gray && stats.chroma < o.dullThreshold)
                photoVibrance = o.vibranceMax * (o.dullThreshold - stats.chroma) / o.dullThreshold;
        }

        // paper compensation : inverse of the darkening and of the saturation loss.
        // the contrast loss (paper black and white) cannot be compensated : the punch gives the impression back.
        let paperGamma = Math.pow(1 / o.dotGain, o.compensation);
        let paperVibrance = gray ? 0 : o.compensation * (1 / o.paperSaturation - 1) + o.punch * 0.5;

        return {
            gamma : photoGamma * paperGamma,
            vibrance : Math.min(o.vibranceCap, photoVibrance + paperVibrance),
            contrast : o.punch,
            sharpen : o.sharpen,
            sharpenRadius : o.sharpenRadius,
            sharpenThreshold : o.sharpenThreshold,
            // detail, for the logs
            photoGamma : photoGamma, photoVibrance : photoVibrance, paperGamma : paperGamma, paperVibrance : paperVibrance
        };
    }

    // ---------- 3. Correction ----------

    correct(imageData, settings)
    {
        let LUM = enhance.PhotoEnhancer.luminance;
        let gamma = settings.gamma === undefined ? 1 : settings.gamma;
        let vibrance = settings.vibrance || 0;
        let contrast = settings.contrast || 0;
        let d = imageData.data;
        let lut = new Uint8ClampedArray(256);

        for (let v = 0; v < 256; v++)
        {
            let x = Math.pow(v / 255, gamma);

            if (contrast)
                x -= contrast * Math.sin(2 * Math.PI * x) / (2 * Math.PI); // S curve, 0 and 1 fixed

            lut[v] = 255 * x;
        }

        for (let i = 0; i < d.length; i += 4)
        {
            if (d[i + 3] === 0) // fully transparent : nothing shows
                continue;

            let r = lut[d[i]], g = lut[d[i + 1]], b = lut[d[i + 2]];

            if (vibrance > 0)
            {
                let max = Math.max(r, g, b), min = Math.min(r, g, b), spread = max - min;
                let k = vibrance * (1 - spread / 255);        // already saturated pixels are barely touched

                if (r >= g && g >= b && spread > 15)          // skin tones protected
                {
                    let t = (g - b) / spread;

                    if (t > 0.17 && t < 0.75)
                        k *= 0.4;
                }

                let l = LUM(r, g, b);
                r = l + (r - l) * (1 + k);
                g = l + (g - l) * (1 + k);
                b = l + (b - l) * (1 + k);
            }

            d[i] = r; d[i + 1] = g; d[i + 2] = b;
        }

        if (settings.sharpen > 0)
            this.sharpen(imageData, settings.sharpen, this.radiusFor(imageData, settings.sharpenRadius), settings.sharpenThreshold, settings.transparent);

        return imageData;
    }

    // true when some pixel is not fully opaque (a PNG with transparency)
    hasAlpha(imageData)
    {
        let d = imageData.data;

        for (let i = 3; i < d.length; i += 4)
            if (d[i] < 255)
                return true;

        return false;
    }

    // ---------- Sharpness : unsharp mask on the luminance ----------

    // the radius is given for a 3000 px long side and scaled, so that the effect is the same on any size
    radiusFor(imageData, radius)
    {
        return radius * Math.max(imageData.width, imageData.height) / 3000;
    }

    gaussianBlur(src, w, h, sigma)
    {
        let R = Math.max(1, Math.ceil(sigma * 3));
        let k = new Float32Array(2 * R + 1);
        let sum = 0;

        for (let i = -R; i <= R; i++)
        {
            k[i + R] = Math.exp(-(i * i) / (2 * sigma * sigma));
            sum += k[i + R];
        }

        for (let i = 0; i < k.length; i++)
            k[i] /= sum;

        let tmp = new Float32Array(w * h), out = new Float32Array(w * h);

        for (let y = 0; y < h; y++)
        {
            let line = y * w;

            for (let x = 0; x < w; x++)
            {
                let acc = 0;

                for (let i = -R; i <= R; i++)
                {
                    let xx = x + i;
                    if (xx < 0) xx = 0; else if (xx >= w) xx = w - 1;
                    acc += src[line + xx] * k[i + R];
                }

                tmp[line + x] = acc;
            }
        }

        for (let y = 0; y < h; y++)
        {
            for (let x = 0; x < w; x++)
            {
                let acc = 0;

                for (let i = -R; i <= R; i++)
                {
                    let yy = y + i;
                    if (yy < 0) yy = 0; else if (yy >= h) yy = h - 1;
                    acc += tmp[yy * w + x] * k[i + R];
                }

                out[y * w + x] = acc;
            }
        }

        return out;
    }

    // amount > 0 : sharpens ; amount < 0 : softens (paper simulation).
    // transparent : the blur is weighted by the alpha, so that the hidden colour of the transparent
    // pixels does not bleed into the edges of the picture (a light halo around it)
    sharpen(imageData, amount, sigma, threshold, transparent)
    {
        if (!amount || sigma < 0.2)
            return imageData;

        threshold = threshold || 0;

        let LUM = enhance.PhotoEnhancer.luminance;
        let d = imageData.data, w = imageData.width, h = imageData.height;
        let n = w * h, L = new Float32Array(n);

        for (let p = 0, i = 0; p < n; p++, i += 4)
            L[p] = LUM(d[i], d[i + 1], d[i + 2]);

        let B;

        if (transparent)
        {
            let W = new Float32Array(n);

            for (let p = 0, i = 3; p < n; p++, i += 4)
            {
                W[p] = d[i] / 255;
                L[p] *= W[p];
            }

            let BL = this.gaussianBlur(L, w, h, sigma);
            let BW = this.gaussianBlur(W, w, h, sigma);

            for (let p = 0; p < n; p++)
            {
                L[p] = W[p] ? L[p] / W[p] : 0;
                BL[p] = BW[p] > 1e-4 ? BL[p] / BW[p] : L[p];
            }

            B = BL;
        }
        else
            B = this.gaussianBlur(L, w, h, sigma);

        for (let p = 0, i = 0; p < n; p++, i += 4)
        {
            if (transparent && d[i + 3] === 0)
                continue;

            let diff = L[p] - B[p], a = Math.abs(diff);

            if (a <= threshold)
                continue;

            // soft threshold : no step between sharpened and untouched areas.
            // same delta on R, G and B : the luminance is sharpened without coloured fringes.
            let delta = (diff > 0 ? a - threshold : threshold - a) * amount;
            d[i] += delta; d[i + 1] += delta; d[i + 2] += delta;
        }

        return imageData;
    }

    // ---------- 4. Paper simulation (preview on screen, ?simulate-paper=1) ----------

    simulatePaper(imageData, transparent)
    {
        let LUM = enhance.PhotoEnhancer.luminance;
        let o = this.options;
        let d = imageData.data;
        let lut = new Float32Array(256);

        for (let v = 0; v < 256; v++)
            lut[v] = 255 * (o.paperBlack + (o.paperWhite - o.paperBlack) * Math.pow(v / 255, o.dotGain));

        for (let i = 0; i < d.length; i += 4)
        {
            let r = lut[d[i]], g = lut[d[i + 1]], b = lut[d[i + 2]];
            let l = LUM(r, g, b), s = o.paperSaturation;

            d[i] = l + (r - l) * s; d[i + 1] = l + (g - l) * s; d[i + 2] = l + (b - l) * s;
        }

        this.sharpen(imageData, -1, this.radiusFor(imageData, o.paperBlur), 0, transparent);

        return imageData;
    }

    // ---------- Loading / output ----------

    _loadImage(url)
    {
        return new Promise((resolve, reject) => {
            let img = new Image();
            let timer = setTimeout(() => reject(new Error("timeout")), this.options.loadTimeout);

            // same settings as fx.Media : the browser answers from its cache, and the canvas is not tainted
            if (url.indexOf("blob:") !== 0 && url.indexOf("data:") !== 0)
                img.crossOrigin = "anonymous";

            img.onload = () => {
                clearTimeout(timer);

                if (!img.naturalWidth || !img.naturalHeight)
                    reject(new Error("empty-image"));
                else
                    resolve(img);
            };

            img.onerror = () => {
                clearTimeout(timer);
                reject(new Error("load-error"));
            };

            img.src = url;
        });
    }

    _toCanvas(img, maxSize)
    {
        let width = img.naturalWidth, height = img.naturalHeight;
        let scale = (maxSize && Math.max(width, height) > maxSize) ? maxSize / Math.max(width, height) : 1;

        let canvas = document.createElement("canvas");
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);

        let ctx = canvas.getContext("2d", { willReadFrequently : true });
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        return canvas;
    }

    // a photo with transparency stays a PNG : a JPEG has no alpha channel and turns it black
    _toBlobUrl(canvas, transparent)
    {
        return new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (!blob)
                    reject(new Error("encoding-error"));
                else
                    resolve((window.URL || window.webkitURL).createObjectURL(blob));
            }, transparent ? "image/png" : "image/jpeg", this.options.jpegQuality);
        });
    }
});
